import { useState, useCallback, useEffect } from 'react';
import FlexService from '../../../services/FlexService';

/**
 * useFlexScripts - Hook for managing FLEX scripts
 *
 * Features:
 * - Load scripts list
 * - CRUD operations
 * - Current script state management
 */
function useFlexScripts(warehouseId = null) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load scripts list
  const loadScripts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await FlexService.listScripts({ warehouseId });
      setScripts(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load scripts:', err);
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  // Load scripts on mount
  useEffect(() => {
    loadScripts();
  }, [loadScripts]);

  // Select a script
  const selectScript = useCallback(async (script) => {
    // Check for unsaved changes
    if (isModified && selectedScript) {
      const confirm = window.confirm('You have unsaved changes. Discard them?');
      if (!confirm) return;
    }

    try {
      // Load full script details if needed
      const fullScript = script.id
        ? await FlexService.getScript(script.id)
        : script;

      setSelectedScript(fullScript);
      setEditorContent(fullScript.scriptBody || '');
      setIsModified(false);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load script:', err);
    }
  }, [isModified, selectedScript]);

  // Update editor content
  const updateContent = useCallback((content) => {
    setEditorContent(content);
    if (selectedScript && content !== selectedScript.scriptBody) {
      setIsModified(true);
    } else {
      setIsModified(false);
    }
  }, [selectedScript]);

  // Create new script
  const createScript = useCallback(async (scriptData) => {
    try {
      const newScript = await FlexService.createScript({
        ...scriptData,
        warehouseId,
      });
      setScripts((prev) => [...prev, newScript]);
      await selectScript(newScript);
      return newScript;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create script:', err);
      throw err;
    }
  }, [warehouseId, selectScript]);

  // Save current script
  const saveScript = useCallback(async () => {
    if (!selectedScript) return;

    setIsSaving(true);
    try {
      const updated = await FlexService.updateScript(selectedScript.id, {
        ...selectedScript,
        scriptBody: editorContent,
      });

      setSelectedScript(updated);
      setIsModified(false);

      // Update in scripts list
      setScripts((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );

      return updated;
    } catch (err) {
      setError(err.message);
      console.error('Failed to save script:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [selectedScript, editorContent]);

  // Delete script
  const deleteScript = useCallback(async (scriptId) => {
    try {
      await FlexService.deleteScript(scriptId);
      setScripts((prev) => prev.filter((s) => s.id !== scriptId));

      // Clear selection if deleted script was selected
      if (selectedScript?.id === scriptId) {
        setSelectedScript(null);
        setEditorContent('');
        setIsModified(false);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete script:', err);
      throw err;
    }
  }, [selectedScript]);

  // Rename script
  const renameScript = useCallback(async (scriptId, newName) => {
    try {
      const script = scripts.find((s) => s.id === scriptId);
      if (!script) return;

      const updated = await FlexService.updateScript(scriptId, {
        ...script,
        name: newName,
      });

      setScripts((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );

      if (selectedScript?.id === scriptId) {
        setSelectedScript(updated);
      }

      return updated;
    } catch (err) {
      setError(err.message);
      console.error('Failed to rename script:', err);
      throw err;
    }
  }, [scripts, selectedScript]);

  // Update script metadata (name, description)
  const updateScriptMeta = useCallback(async (scriptId, updates) => {
    try {
      const script = scripts.find((s) => s.id === scriptId);
      if (!script) return;

      const updated = await FlexService.updateScript(scriptId, {
        ...script,
        ...updates,
      });

      setScripts((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );

      if (selectedScript?.id === scriptId) {
        setSelectedScript(updated);
      }

      return updated;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update script:', err);
      throw err;
    }
  }, [scripts, selectedScript]);

  // Create new empty script (without saving)
  const newScript = useCallback(() => {
    if (isModified && selectedScript) {
      const confirm = window.confirm('You have unsaved changes. Discard them?');
      if (!confirm) return;
    }

    setSelectedScript({
      name: 'Untitled',
      description: '',
      scriptBody: '// New FLEX script\n',
    });
    setEditorContent('// New FLEX script\n');
    setIsModified(false);
  }, [isModified, selectedScript]);

  // Toggle lock status
  const toggleLock = useCallback(async (scriptId) => {
    try {
      const updated = await FlexService.toggleLock(scriptId);

      setScripts((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );

      if (selectedScript?.id === scriptId) {
        setSelectedScript(updated);
      }

      return updated;
    } catch (err) {
      setError(err.message);
      console.error('Failed to toggle lock:', err);
      throw err;
    }
  }, [selectedScript]);

  return {
    scripts,
    loading,
    error,
    selectedScript,
    editorContent,
    isModified,
    isSaving,
    loadScripts,
    selectScript,
    updateContent,
    createScript,
    saveScript,
    deleteScript,
    renameScript,
    updateScriptMeta,
    newScript,
    toggleLock,
    setError,
  };
}

export default useFlexScripts;
